import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const LandingPage = () => {
  const [threatsBlocked, setThreatsBlocked] = useState(12847);
  const [showThreatPopup, setShowThreatPopup] = useState(false);
  const [currentThreat, setCurrentThreat] = useState({});

  useEffect(() => {
    const interval = setInterval(() => {
      setThreatsBlocked(prev => prev + Math.floor(Math.random() * 3));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Threat popup animation
  useEffect(() => {
    const threats = [
      { type: 'Ransomware', severity: 'Critical', location: 'Server-01' },
      { type: 'Phishing Attempt', severity: 'High', location: 'Email Gateway' },
      { type: 'Malware Detected', severity: 'Medium', location: 'Workstation-05' },
      { type: 'Unauthorized Access', severity: 'Critical', location: 'Network Firewall' },
      { type: 'SQL Injection', severity: 'High', location: 'Web Application' }
    ];

    const showThreat = () => {
      const randomThreat = threats[Math.floor(Math.random() * threats.length)];
      setCurrentThreat(randomThreat);
      setShowThreatPopup(true);
      
      setTimeout(() => {
        setShowThreatPopup(false);
      }, 4000);
    };

    // Show first popup after 2 seconds
    const firstTimeout = setTimeout(showThreat, 2000);

    // Then show randomly every 8-15 seconds
    const interval = setInterval(() => {
      showThreat();
    }, Math.random() * 7000 + 8000);

    return () => {
      clearTimeout(firstTimeout);
      clearInterval(interval);
    };
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Threat Detection Popup */}
      {showThreatPopup && (
        <div className="fixed top-24 right-8 z-50 animate-slide-in-right">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 shadow-2xl rounded-lg max-w-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-500 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-bold text-red-800">
                  🚨 Threat Detected & Blocked
                </p>
                <p className="text-sm text-red-700 mt-1">
                  <span className="font-semibold">{currentThreat.type}</span>
                </p>
                <p className="text-xs text-red-600 mt-1">
                  Severity: {currentThreat.severity} | {currentThreat.location}
                </p>
                <p className="text-xs text-green-600 font-semibold mt-2">
                  ✓ Automatically neutralized
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white shadow-md z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <span className="text-2xl font-bold text-blue-600">CyberGuard - Powered by Aslan Defense</span>
            </div>
            <div className="hidden md:flex space-x-8">
              <button onClick={() => scrollToSection('features')} className="text-gray-700 hover:text-blue-600 transition">Features</button>
              <button onClick={() => scrollToSection('pricing')} className="text-gray-700 hover:text-blue-600 transition">Pricing</button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-700 hover:text-blue-600 transition">FAQ</button>
              <Link to="/login" className="text-gray-700 hover:text-blue-600 transition">Login</Link>
            </div>
            <div className="flex space-x-4">
              <Link to="/login" className="text-blue-600 border border-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition">
                Login
              </Link>
              <Link to="/pricing" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition">
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-32 pb-20 px-4 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-6">
            <div className="inline-flex items-center space-x-4 bg-white px-6 py-3 rounded-full shadow-lg">
              <span className="text-sm font-semibold text-gray-600">🛡️ SOC 2 Compliant</span>
              <span className="text-sm font-semibold text-gray-600">✓ 99.9% Uptime</span>
              <span className="text-sm font-semibold text-gray-600">🔒 GDPR Ready</span>
            </div>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Enterprise-Grade Security<br />
            <span className="text-blue-600">for Small Business Budgets</span>
          </h1>
          
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            24/7 threat detection and response without the enterprise cost. Set up in 5 minutes, protect your business in real-time.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
            <Link to="/pricing" className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition shadow-lg">
              Start Free Trial
            </Link>
            <button onClick={() => scrollToSection('pricing')} className="bg-white border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition">
              See Pricing
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-xl p-6 inline-block">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                {threatsBlocked.toLocaleString()}+
              </div>
              <div className="text-gray-600">Threats Blocked Today</div>
            </div>
          </div>
        </div>
      </div>

      {/* Problem/Solution Section */}
      <div className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">The Challenge</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-red-500 text-xl mr-3">✗</span>
                  <span className="text-gray-700">Small businesses face the same threats as enterprises</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 text-xl mr-3">✗</span>
                  <span className="text-gray-700">Can't afford $50K+ security solutions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 text-xl mr-3">✗</span>
                  <span className="text-gray-700">No in-house security team</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 text-xl mr-3">✗</span>
                  <span className="text-gray-700">Overwhelmed by complex security tools</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">CyberGuard Solution</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-green-500 text-xl mr-3">✓</span>
                  <span className="text-gray-700">Set up in 5 minutes, not 5 days</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 text-xl mr-3">✓</span>
                  <span className="text-gray-700">Starting at $29/month</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 text-xl mr-3">✓</span>
                  <span className="text-gray-700">No IT expertise required</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 text-xl mr-3">✓</span>
                  <span className="text-gray-700">Real-time protection with AI</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Complete Security Suite</h2>
            <p className="text-xl text-gray-600">Everything you need to protect your business</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-100 hover:shadow-xl transition">
              <div className="text-4xl mb-4">🔍</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">24/7 Threat Monitoring</h3>
              <p className="text-gray-600 mb-4">Real-time detection across all devices with AI-powered threat hunting and instant email alerts.</p>
              <ul className="space-y-2 text-gray-600">
                <li>• Real-time scanning</li>
                <li>• AI threat detection</li>
                <li>• Instant notifications</li>
              </ul>
            </div>

            {/* Feature 2 */}
            <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-100 hover:shadow-xl transition">
              <div className="text-4xl mb-4">⚡</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Automated Response</h3>
              <p className="text-gray-600 mb-4">One-click threat remediation with automatic quarantine and zero-touch security.</p>
              <ul className="space-y-2 text-gray-600">
                <li>• Auto-quarantine malware</li>
                <li>• One-click fixes</li>
                <li>• Zero-touch security</li>
              </ul>
            </div>

            {/* Feature 3 */}
            <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-100 hover:shadow-xl transition">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Complete Visibility</h3>
              <p className="text-gray-600 mb-4">Dashboard showing all threats, device monitoring, and cloud security tracking.</p>
              <ul className="space-y-2 text-gray-600">
                <li>• Real-time dashboard</li>
                <li>• Device monitoring</li>
                <li>• Cloud security</li>
              </ul>
            </div>

            {/* Feature 4 */}
            <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-100 hover:shadow-xl transition">
              <div className="text-4xl mb-4">📋</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Compliance Made Easy</h3>
              <p className="text-gray-600 mb-4">GDPR compliance templates, automated audit logs, and exportable reports.</p>
              <ul className="space-y-2 text-gray-600">
                <li>• GDPR templates</li>
                <li>• Automated logs</li>
                <li>• Export reports</li>
              </ul>
            </div>

            {/* Feature 5 */}
            <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-100 hover:shadow-xl transition">
              <div className="text-4xl mb-4">🔐</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Two-Factor Authentication</h3>
              <p className="text-gray-600 mb-4">Enterprise-grade 2FA with Google Authenticator integration and backup codes.</p>
              <ul className="space-y-2 text-gray-600">
                <li>• Google Authenticator</li>
                <li>• Backup codes</li>
                <li>• Enhanced security</li>
              </ul>
            </div>

            {/* Feature 6 */}
            <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-100 hover:shadow-xl transition">
              <div className="text-4xl mb-4">💬</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Support</h3>
              <p className="text-gray-600 mb-4">Live chat support, security guidance, and weekly security summaries.</p>
              <ul className="space-y-2 text-gray-600">
                <li>• Live chat available</li>
                <li>• Security experts</li>
                <li>• Weekly summaries</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div id="pricing" className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-gray-600">No hidden fees. Cancel anytime. 14-day free trial on all plans.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Starter Plan */}
            <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Starter</h3>
              <div className="text-4xl font-bold text-blue-600 mb-6">
                $29<span className="text-lg text-gray-600">/month</span>
              </div>
              <p className="text-sm text-gray-500 mb-6">14-day free trial included</p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">Up to 3 devices</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">Basic threat detection</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">Weekly reports</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">Email support</span>
                </li>
              </ul>
              <Link to="/checkout?plan=starter" className="block w-full text-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition">
                Start Free Trial
              </Link>
            </div>

            {/* Business Plan */}
            <div className="bg-blue-600 p-8 rounded-lg shadow-xl border-4 border-blue-700 transform scale-105">
              <div className="bg-yellow-400 text-blue-900 text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
                MOST POPULAR
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Business</h3>
              <div className="text-4xl font-bold text-white mb-6">
                $49<span className="text-lg text-blue-200">/month</span>
              </div>
              <p className="text-sm text-blue-100 mb-6">14-day free trial included</p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <span className="text-white mr-2">✓</span>
                  <span className="text-white">Up to 10 users</span>
                </li>
                <li className="flex items-start">
                  <span className="text-white mr-2">✓</span>
                  <span className="text-white">Advanced threat detection</span>
                </li>
                <li className="flex items-start">
                  <span className="text-white mr-2">✓</span>
                  <span className="text-white">Real-time alerts</span>
                </li>
                <li className="flex items-start">
                  <span className="text-white mr-2">✓</span>
                  <span className="text-white">Two-factor authentication</span>
                </li>
                <li className="flex items-start">
                  <span className="text-white mr-2">✓</span>
                  <span className="text-white">Priority support</span>
                </li>
                <li className="flex items-start">
                  <span className="text-white mr-2">✓</span>
                  <span className="text-white">Compliance reports</span>
                </li>
              </ul>
              <Link to="/checkout?plan=business" className="block w-full text-center bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition">
                Start Free Trial
              </Link>
            </div>

            {/* Enterprise Plan */}
            <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Enterprise</h3>
              <div className="text-4xl font-bold text-blue-600 mb-6">
                $149<span className="text-lg text-gray-600">/month</span>
              </div>
              <p className="text-sm text-gray-500 mb-6">14-day free trial included</p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">Unlimited users</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">Custom integrations</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">Dedicated account manager</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">API access</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">White-label option</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">24/7 phone support</span>
                </li>
              </ul>
              <Link to="/checkout?plan=enterprise" className="block w-full text-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition">
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div id="faq" className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Everything you need to know</p>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Do I need technical knowledge to use CyberGuard?</h3>
              <p className="text-gray-700">No! CyberGuard is designed for non-technical users. Setup takes just 5 minutes with our guided process, and our AI handles all the complex security work automatically.</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-2">How long does setup take?</h3>
              <p className="text-gray-700">Setup takes approximately 5 minutes. Simply sign up, download our lightweight agent, and you're protected. No complex configuration needed.</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Can I cancel anytime?</h3>
              <p className="text-gray-700">Yes! There are no long-term contracts. You can cancel your subscription at any time from your account settings.</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-2">What if I have more than 10 users?</h3>
              <p className="text-gray-700">You can upgrade to our Enterprise plan which supports unlimited users, or contact our sales team for a custom solution tailored to your needs.</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Is my data secure?</h3>
              <p className="text-gray-700">Absolutely. We're SOC 2 compliant, use end-to-end encryption, and never sell your data. Your security is our top priority.</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-2">What kind of threats can CyberGuard detect?</h3>
              <p className="text-gray-700">CyberGuard detects malware, ransomware, phishing attempts, unauthorized access, data breaches, and suspicious network activity. Our AI continuously learns about new threats.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="py-20 px-4 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Start Protecting Your Business Today
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join hundreds of businesses trusting CyberGuard for their security
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/pricing" className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition shadow-lg">
              Start Free Trial
            </Link>
            <button onClick={() => scrollToSection('pricing')} className="bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-800 transition border-2 border-white">
              View Pricing
            </button>
          </div>
          <p className="text-blue-200 mt-6">14-day free trial • No credit card required • Setup in 5 minutes</p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h4 className="text-xl font-bold mb-4">CyberGuard - Powered by Aslan Defense</h4>
              <p className="text-gray-400">Enterprise-grade security for small business budgets.</p>
            </div>
            
            <div>
              <h5 className="font-bold mb-4">Company</h5>
              <div className="space-y-4 text-gray-400">
                <div>
                  <h6 className="font-semibold text-white mb-2">About Us</h6>
                  <p className="text-sm">
                    CyberGuard by Aslan Defense is revolutionizing cybersecurity with AI-enhanced instant threat detection. 
                    Trusted by over 500 small, medium, and enterprise companies, we've become the leading solution for businesses 
                    that need enterprise-grade security without enterprise costs. Our cutting-edge AI technology provides 24/7 
                    protection, automatically neutralizing threats before they impact your business.
                  </p>
                </div>
                <div>
                  <h6 className="font-semibold text-white mb-2">Contact</h6>
                  <a href="mailto:support@aslandefense.com" className="hover:text-white transition">
                    support@aslandefense.com
                  </a>
                </div>
                <div>
                  <h6 className="font-semibold text-white mb-2">Blog</h6>
                  <p className="text-sm italic">Coming Soon</p>
                </div>
              </div>
            </div>

            <div>
              <h5 className="font-bold mb-4">Product</h5>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => scrollToSection('features')} className="hover:text-white transition">Features</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white transition">Pricing</button></li>
                <li><button onClick={() => scrollToSection('faq')} className="hover:text-white transition">FAQ</button></li>
                <li><Link to="/signup" className="hover:text-white transition">Sign Up</Link></li>
                <li><Link to="/login" className="hover:text-white transition">Login</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2026 CyberGuard - Powered by Aslan Defense. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;